/**
 * Owner Defect Lifecycle Management page.
 * Full defect CRUD: create, update, rename, delete, archive via modals.
 * Status transitions: OPEN ↔ UNSURE ↔ RESOLVED.
 *
 * Route: #/owner/defects
 * Requirements: 20.1, 20.2
 */

import { getUser, isOwner } from '../services/auth.js';
import {
  getAircraft,
  getAllDefects,
  createDefect,
  updateDefect,
  updateDefectStatus,
  archiveDefect,
  deleteDefect,
} from '../services/api.js';
import { navigate } from '../services/router.js';
import { formatDateUK } from '../utils/format.js';
import { DEFECT_STATUS, AIRCRAFT_STATUS } from '../utils/constants.js';
import { createDataGrid } from '../components/data-grid.js';
import { openDetailModal } from '../components/detail-modal.js';
import { renderDefectStatusBadge } from '../components/defect-status-badge.js';

// ── Exported pure helpers for testability ─────────────

/**
 * Status transition order: OPEN → UNSURE → RESOLVED.
 */
const STATUS_ORDER = [DEFECT_STATUS.OPEN, DEFECT_STATUS.UNSURE, DEFECT_STATUS.RESOLVED];

/**
 * Get the next status in the forward transition chain.
 * OPEN → UNSURE → RESOLVED. Returns null if already at RESOLVED.
 * @param {string} currentStatus
 * @returns {string|null}
 */
export function getNextStatus(currentStatus) {
  const idx = STATUS_ORDER.indexOf(currentStatus);
  if (idx === -1 || idx >= STATUS_ORDER.length - 1) return null;
  return STATUS_ORDER[idx + 1];
}

/**
 * Get the previous status in the reverse transition chain.
 * RESOLVED → UNSURE → OPEN. Returns null if already at OPEN.
 * @param {string} currentStatus
 * @returns {string|null}
 */
export function getPreviousStatus(currentStatus) {
  const idx = STATUS_ORDER.indexOf(currentStatus);
  if (idx <= 0) return null;
  return STATUS_ORDER[idx - 1];
}

/**
 * Check whether a status transition is valid.
 * Valid transitions are one step forward or backward in the chain:
 * OPEN ↔ UNSURE ↔ RESOLVED.
 * @param {string} from
 * @param {string} to
 * @returns {boolean}
 */
export function canTransitionStatus(from, to) {
  if (!from || !to) return false;
  const fromIdx = STATUS_ORDER.indexOf(from);
  const toIdx = STATUS_ORDER.indexOf(to);
  if (fromIdx === -1 || toIdx === -1) return false;
  return Math.abs(fromIdx - toIdx) === 1;
}

/**
 * Format a defect for display in the owner grid.
 * @param {Object} defect
 * @returns {Object}
 */
export function formatDefectForOwnerDisplay(defect) {
  if (!defect) return {};
  return {
    ...defect,
    display_title: defect.title || '—',
    display_description: defect.description || '—',
    display_aircraft: defect.aircraft?.registration || '—',
    display_reported_by: defect.pilots?.name || 'Unknown',
    display_date: defect.opened_at ? formatDateUK(defect.opened_at) : '—',
    display_status: defect.status || '—',
  };
}

/**
 * Filter defects to exclude archived ones.
 * @param {Object[]} defects
 * @returns {Object[]}
 */
export function filterActiveDefects(defects) {
  if (!Array.isArray(defects)) return [];
  return defects.filter((d) => d && !d.archived);
}

// ── Page state ────────────────────────────────────────

let aircraftList = [];
let allDefects = [];

// ── Main render ───────────────────────────────────────

export async function renderOwnerDefects() {
  const app = document.getElementById('app');
  if (!app) return;

  if (!isOwner()) {
    app.innerHTML = `
      <div class="container">
        <h2>Access Denied</h2>
        <p>Only owners can access defect lifecycle management.</p>
      </div>
    `;
    return;
  }

  app.innerHTML = `
    <div class="container">
      <h2>Defect Management (Owner)</h2>
      <div id="owner-defects-msg"></div>
      <div style="margin-bottom:1rem;">
        <button id="btn-create-defect" class="btn btn--primary">Create Defect</button>
      </div>
      <div class="card">
        <h3>All Defects</h3>
        <div id="owner-defects-grid">Loading…</div>
      </div>
    </div>
  `;

  try {
    aircraftList = await getAircraft();
  } catch (err) {
    showMsg('Could not load aircraft: ' + err.message, true);
  }

  await loadDefects();

  const createBtn = document.getElementById('btn-create-defect');
  if (createBtn) {
    createBtn.addEventListener('click', () => openCreateDefectModal());
  }
}

// ── Load and render defects ───────────────────────────

async function loadDefects() {
  const container = document.getElementById('owner-defects-grid');
  if (!container) return;

  try {
    const raw = await getAllDefects();
    allDefects = filterActiveDefects(raw);
    renderDefectsGrid(container, allDefects);
  } catch (err) {
    container.innerHTML = `<p class="error">Failed to load defects: ${err.message}</p>`;
  }
}

function renderDefectsGrid(container, defects) {
  container.innerHTML = '';

  const columns = [
    { key: 'title', label: 'Title', format: (v) => v || '—' },
    {
      key: 'aircraft_reg',
      label: 'Aircraft',
      html: true,
      format: (_, row) => {
        const reg = row.aircraft?.registration;
        const id = row.aircraft?.id || row.aircraft_id;
        if (!reg) return '—';
        if (!id) return reg;
        return `<a href="#/aircraft/${id}" class="aircraft-link">${reg}</a>`;
      },
    },
    {
      key: 'description',
      label: 'Description',
      format: (v) => {
        if (!v) return '—';
        return v.length > 60 ? v.slice(0, 60) + '…' : v;
      },
    },
    {
      key: 'opened_at',
      label: 'Reported',
      format: (v) => (v ? formatDateUK(v) : '—'),
    },
    {
      key: 'reported_by_name',
      label: 'Reported By',
      format: (_, row) => row.pilots?.name || 'Unknown',
    },
    {
      key: 'status',
      label: 'Status',
      html: true,
      format: (v) => renderDefectStatusBadge(v),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      html: true,
      format: (_, row) => {
        const btns = [];
        btns.push(`<button class="btn btn-sm btn-edit-defect" data-id="${row.id}">Edit</button>`);

        const next = getNextStatus(row.status);
        if (next) {
          btns.push(`<button class="btn btn-sm btn-advance-status" data-id="${row.id}" data-status="${next}">→ ${next}</button>`);
        }
        const prev = getPreviousStatus(row.status);
        if (prev) {
          btns.push(`<button class="btn btn-sm btn-revert-status" data-id="${row.id}" data-status="${prev}">← ${prev}</button>`);
        }

        btns.push(`<button class="btn btn-sm btn-danger btn-archive-defect" data-id="${row.id}">Archive</button>`);
        return btns.join(' ');
      },
    },
  ];

  const grid = createDataGrid({
    columns,
    data: defects,
    defaultSort: 'opened_at',
    defaultOrder: 'desc',
    emptyMessage: 'No defects found',
    filterable: true,
  });

  container.appendChild(grid);

  // Event delegation
  container.addEventListener('click', async (e) => {
    const editBtn = e.target.closest('.btn-edit-defect');
    if (editBtn) {
      const defect = allDefects.find((d) => d.id === editBtn.dataset.id);
      if (defect) openEditDefectModal(defect);
      return;
    }

    const advanceBtn = e.target.closest('.btn-advance-status');
    if (advanceBtn) {
      await handleStatusChange(advanceBtn.dataset.id, advanceBtn.dataset.status);
      return;
    }

    const revertBtn = e.target.closest('.btn-revert-status');
    if (revertBtn) {
      await handleStatusChange(revertBtn.dataset.id, revertBtn.dataset.status);
      return;
    }

    const archiveBtn = e.target.closest('.btn-archive-defect');
    if (archiveBtn) {
      await handleArchive(archiveBtn.dataset.id);
    }
  });
}

// ── Create Defect modal ───────────────────────────────

function getAircraftOptions() {
  return aircraftList
    .filter((ac) => {
      if (ac.meta && ac.meta.retired) return false;
      if (ac.name && ac.name.includes('[RETIRED]')) return false;
      return true;
    })
    .map((ac) => ({ value: ac.id, label: `${ac.registration} (${ac.type})` }));
}

function openCreateDefectModal() {
  const user = getUser();
  const fields = [
    { name: 'aircraft_id', label: 'Aircraft', type: 'select', options: getAircraftOptions(), required: true },
    { name: 'title', label: 'Defect Title', type: 'text', required: true, placeholder: 'Brief summary' },
    { name: 'description', label: 'Description', type: 'textarea', required: true, placeholder: 'Describe the defect…' },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: DEFECT_STATUS.OPEN, label: 'Open' },
        { value: DEFECT_STATUS.UNSURE, label: 'Unsure' },
        { value: DEFECT_STATUS.RESOLVED, label: 'Resolved' },
      ],
      required: true,
    },
  ];

  openDetailModal({
    title: 'Create Defect',
    fields,
    onSave: async (formData) => {
      await createDefect({
        aircraft_id: formData.aircraft_id,
        title: formData.title,
        description: formData.description,
        status: formData.status,
        reported_by: user.id,
      });
      showMsg('Defect created successfully.');
      await loadDefects();
    },
  });
}

// ── Edit Defect modal ─────────────────────────────────

function openEditDefectModal(defect) {
  const fields = [
    { name: 'title', label: 'Defect Title', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea', required: true },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: DEFECT_STATUS.OPEN, label: 'Open' },
        { value: DEFECT_STATUS.UNSURE, label: 'Unsure' },
        { value: DEFECT_STATUS.RESOLVED, label: 'Resolved' },
      ],
      required: true,
    },
  ];

  openDetailModal({
    title: `Edit Defect — ${defect.title || 'Untitled'}`,
    fields,
    values: {
      title: defect.title || '',
      description: defect.description || '',
      status: defect.status || DEFECT_STATUS.OPEN,
    },
    onSave: async (formData) => {
      await updateDefect(defect.id, {
        title: formData.title,
        description: formData.description,
        status: formData.status,
      });
      showMsg('Defect updated successfully.');
      await loadDefects();
    },
    onDelete: async () => {
      await deleteDefect(defect.id);
      showMsg('Defect deleted.');
      await loadDefects();
    },
  });
}

// ── Status change handler ─────────────────────────────

async function handleStatusChange(defectId, newStatus) {
  try {
    await updateDefectStatus(defectId, newStatus);
    showMsg(`Defect status changed to ${newStatus}.`);
    await loadDefects();
  } catch (err) {
    showMsg(`Failed to update status: ${err.message}`, true);
  }
}

// ── Archive handler ───────────────────────────────────

async function handleArchive(defectId) {
  try {
    await archiveDefect(defectId);
    showMsg('Defect archived.');
    await loadDefects();
  } catch (err) {
    showMsg(`Failed to archive defect: ${err.message}`, true);
  }
}

// ── UI helpers ────────────────────────────────────────

function showMsg(text, isError = false) {
  const el = document.getElementById('owner-defects-msg');
  if (!el) return;
  el.textContent = text;
  el.className = isError ? 'error' : 'success';
  setTimeout(() => {
    el.textContent = '';
    el.className = '';
  }, 5000);
}
